import { RoutalPalette } from '@nx-smartmonkey/ui';
import { animated, useSpring, useSprings } from '@react-spring/web';
import { useDrag } from '@use-gesture/react';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { clamp } from 'lodash';
import { useDraggableState } from '../hooks/useDraggableState';
import { useDriversState } from '../hooks/useDriversState';

export type StateType = `FullWindow` | `HalfWindow` | `NoWindow`;

type States = {
  [key in StateType]: number;
};

const DEFAULT_FOOTER_HEIGHT = 70;
const DEFAULT_TOP_BUTTONS_HEIGHT = 10;

const FULL_WINDOW_HEIGHT_LOSS = 0;
const HALF_WINDOW_HEIGHT_LOSS = 0.5;

const MIN_TRIGGER_SCROLL_NONDRAGGABLE = 20;

interface DraggableContainerProp {
  // All information to be shown
  drags: React.ReactNode[]; //Array of draggable elements
  details: React.ReactNode[]; //Array of the details of the stops
  footers?: React.ReactNode[]; //Array of the footers of the stops
  onChanges?: (() => void)[];

  sizeOfFooter?: number;
  sizeOfTopButtons?: number;

  halfWindowHeightLoss?: number;
  fullWindowHeightLoss?: number;

  initScroll?: number;

  footer?: boolean;
  swipe?: boolean; //Enables the swipe between the draggable elements
  swipeIndex?: number; //Index of the actual draggable element
  style?: React.CSSProperties;
  hideHeader?: boolean;
  respectFooter?: boolean; // If the footer height patch should be respected **THIS IS TEMPORAL TODO REFACTOR**
}

export const DraggableContainer = ({
  drags,
  details,
  footers,
  onChanges,
  sizeOfFooter = DEFAULT_FOOTER_HEIGHT,
  sizeOfTopButtons = DEFAULT_TOP_BUTTONS_HEIGHT,
  fullWindowHeightLoss = FULL_WINDOW_HEIGHT_LOSS,
  halfWindowHeightLoss = HALF_WINDOW_HEIGHT_LOSS,
  initScroll = 0,
  footer = true,
  swipe = false,
  swipeIndex = 0,
  hideHeader = false,
  style,
  respectFooter = true,
}: DraggableContainerProp) => {
  const [headerHeight, setHeaderHeight] = useState(70);

  interface DraggableDivProps {
    previousHeaderHeight: number;
    setPreviousHeaderHeight: (value: number) => void;
  }

  //Draggable main component
  const DraggableDiv = ({ setPreviousHeaderHeight, previousHeaderHeight }: DraggableDivProps) => {
    const { driversState } = useDriversState();
    const { state, storeState } = useDraggableState(`HalfWindow`);
    const currentIndex = swipeIndex ? swipeIndex : 0;

    let header: HTMLDivElement | undefined = undefined;
    let content: HTMLDivElement | undefined = undefined;
    const mainContainer = useRef<HTMLDivElement>(null);
    const [width, setWidth] = useState(driversState.width);
    const [headerHeight, setHeaderHeight] = useState(previousHeaderHeight);

    const innerHeight = driversState.height;

    // The height of the draggable elements
    const states: States = {
      FullWindow: innerHeight * fullWindowHeightLoss + sizeOfTopButtons,
      HalfWindow: innerHeight * halfWindowHeightLoss,
      NoWindow: innerHeight - (headerHeight + sizeOfFooter),
    };

    //This pages are basically the draggable elements and its parts
    const pages = drags.map((drag, index) => ({
      draggable: drag,
      notDraggable: details[index],
      footer: footers?.[index] ?? null,
    }));

    //The current index of the draggable element
    const [index, setIndex] = useState(currentIndex);
    const [currentY, setCurrentY] = useState(states[state]);

    const [setBorderShadow, setSetBorderShadow] = useState(false);
    const [triggerChange, setTriggerChange] = useState(false); // There has been a swipe
    const [animationMoving, setAnimationMoving] = useState(false); // The animation is moving

    //This is the component that manages the draggable, x and y are the position of the draggable element
    const [props, api] = useSprings(swipe ? pages.length : 1, (i) => ({
      x: swipe ? (i - index) * width : 0,
      y: states[state],
      onChange: () => {
        setCurrentY(props[i].y.get());
      },
      onStart: () => {
        setAnimationMoving(true);
      },
      onRest: () => {
        setAnimationMoving(false);
      },
    }));
    const dynamicHeight = useSpring({
      height: innerHeight - currentY - headerHeight - sizeOfFooter,
      config: { duration: 0 },
    });

    //Animations
    const fadeIn = useSpring({
      opacity: currentY <= states.FullWindow + 10 ? 1 : 0,
    });

    const fadeOut = useSpring({
      opacity: currentY <= states.FullWindow + 10 && hideHeader ? 0 : 1,
    });

    // Vertical Draggable
    useEffect(() => {
      if (header) {
        setHeaderHeight(header.clientHeight);
        setPreviousHeaderHeight(header.clientHeight);
        setAnimationMoving(true);
        api.start({ y: states[state] });
      }

      const handleResize = () => {
        if (mainContainer.current) {
          setWidth(mainContainer.current.clientWidth);
        }
      };

      window.addEventListener(`resize`, handleResize);
      handleResize();

      return () => {
        window.removeEventListener(`resize`, handleResize);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      // Si el swipe esta deshabilitado, es que solo hay un elemento draggable, por tanto se muestra
      api.start((i) => ({ x: swipe ? (i - index) * width : 0 }));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [width]);

    useEffect(() => {
      api.start({ y: states[state] });

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [states.FullWindow, states.HalfWindow, states.NoWindow, state]);

    useEffect(() => {
      //Cuando se ha hecho un swipe, y se ha acabado la animacion...
      if (triggerChange && !animationMoving) {
        onChanges && onChanges[index]();
        setTriggerChange(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [triggerChange, animationMoving]);

    useLayoutEffect(() => {
      if (content) {
        content.scrollTop = initScroll;
      }
    }, [content]);

    const bind = useDrag(
      (dragState) => {
        if (!swipe || dragState.axis === `y`) {
          // Only in the last instance of the drag, it considers the 'down' (if the user is dragging) false,
          // at that moment we check where we want the content to end up
          if (!dragState.down) {
            // First it checks if the closest state is different than the current one
            const differences = {
              FullWindow: Math.abs(states.FullWindow - currentY),
              HalfWindow: Math.abs(states.HalfWindow - currentY),
              NoWindow: Math.abs(states.NoWindow - currentY),
            };

            const closestState = Object.keys(differences).reduce((a, b) =>
              differences[a as StateType] < differences[b as StateType] ? a : b
            ) as StateType;

            if (closestState !== state) {
              storeState(closestState);
            }
            // In case the state is the same as the closest one, it checks the velocity.
            // In case the velocity is above a certain threshold, it changes the state to the following one.
            else if (dragState.velocity[1] > 0.2) {
              // To the upper one
              if (dragState.direction[1] === -1) {
                if (state === `HalfWindow`) storeState(`FullWindow`);
                if (state === `NoWindow`) storeState(`HalfWindow`);
              }
              // To the lower one
              else {
                if (state === `HalfWindow`) storeState(`NoWindow`);
                if (state === `FullWindow`) storeState(`HalfWindow`);
              }
            }
          }
          // If the user is dragging, it moves the content
          api.start({
            y: dragState.down ? dragState.movement[1] + states[state] : states[state],
            immediate: dragState.down,
          });
        } else {
          // If the swipe is enabled, it moves the content horizontally, if the user has dragged enough, it changes the index
          if (swipe && dragState.active && Math.abs(dragState.movement[0]) > width / 4) {
            setIndex(
              clamp(
                index + (dragState.direction[0] > 0 ? -1 : 1),
                pages[index - 1] && pages[index - 1].draggable !== undefined ? index - 1 : index,
                pages[index + 1] && pages[index + 1].draggable !== undefined ? index + 1 : index
              )
            );
            // It sets the trigger change to true, so it can be used in the useEffect
            setTriggerChange(true);
            dragState.cancel();
          }

          api.start((i) => {
            const x = (i - index) * width + (dragState.active ? dragState.movement[0] : 0);
            return { x };
          });
        }
      },
      { filterTaps: true, threshold: 10, axis: `lock` }
    );

    // Bind it to a component
    return (
      <>
        <animated.div
          ref={mainContainer}
          style={{
            ...fadeIn,
            backgroundColor: RoutalPalette.white,
            position: `absolute`,
            top: 0,
            left: 0,
            right: 0,
            height: currentY,
            zIndex: 1002,
            visibility: fadeIn.opacity.get() === 0 ? `hidden` : `unset`,
          }}
        />
        {props.map(
          ({ x, y }, i) =>
            pages[i].draggable && (
              <React.Fragment key={i}>
                <animated.div
                  style={{
                    ...style,
                    x:
                      props[0] && props[0].x.animation.values[0] && props[0].x.animation.values[0].getValue() === 0
                        ? undefined
                        : x,
                    y,
                    position: `absolute`,
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    height: `${innerHeight + 120}px`,
                    zIndex: 1000,
                    backgroundColor: RoutalPalette.white,
                    transition: `border-radius 0.5s ease`,
                    borderRadius: states[state] !== states.FullWindow ? `20px 20px 0 0` : 0,
                  }}
                >
                  <div
                    style={{
                      touchAction: `none`,
                      width: `100%`,
                      borderBottom: `0.5px solid ${RoutalPalette.white}`,
                      boxShadow: setBorderShadow ? `0px 2px 4px rgba(0, 0, 0, 0.1)` : ``,
                    }}
                  >
                    <animated.div
                      ref={(element) => {
                        // It has to conditionally assign the ref in order to assign only the current element
                        if (i === index && element) {
                          header = element;
                        }
                      }}
                      {...bind()}
                      style={{
                        ...fadeOut,
                        transition: `box-shadow 0.5s ease`,
                        touchAction: `none`,
                        width: `100%`,
                        borderBottom: `0.5px solid ${RoutalPalette.white}`,
                      }}
                    >
                      {pages[i].draggable}
                    </animated.div>
                  </div>
                  <animated.div
                    ref={(element) => {
                      if (i === index && element) {
                        content = element;
                      }
                    }}
                    onScroll={(event) => {
                      setSetBorderShadow(event.currentTarget.scrollTop > MIN_TRIGGER_SCROLL_NONDRAGGABLE);
                    }}
                    style={{
                      // Calculamos el height del div proporcional al tamaño del div draggable. Restandole el tamaño del header y del footer.
                      ...dynamicHeight,
                      height:
                        pages[i].footer === null && respectFooter
                          ? `${dynamicHeight.height.get() + sizeOfFooter}px`
                          : dynamicHeight.height, // TODO Esto es un parche para que no reserve espacio en el footer en caso de que no aparezca ninguno.
                      overflowY: `auto`,
                      overflowX: `hidden`,
                      paddingBottom: `10px`,
                    }}
                  >
                    {pages[i].notDraggable}
                  </animated.div>
                </animated.div>
                {footer
                  ? pages[i].footer && (
                      <animated.div
                        style={{
                          x,
                          position: `absolute`,
                          right: 0,
                          left: 0,
                          bottom: 0,
                          zIndex: 1004,
                          height: `fit-content`,
                          display: `flex`,
                          alignItems: `flex-end`,
                          justifyContent: `center`,
                          backgroundColor: RoutalPalette.white,
                          boxShadow: undefined,
                        }}
                      >
                        {pages[i].footer}
                      </animated.div>
                    )
                  : null}
              </React.Fragment>
            )
        )}
      </>
    );
  };
  return <DraggableDiv previousHeaderHeight={headerHeight} setPreviousHeaderHeight={setHeaderHeight} />;
};
